import { useEffect, useRef, useState, useMemo } from "react";
import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import { detectToolsOrigin } from "../../../utils/helper.js";
import axios from "axios";
import "../calculator.scss";

// ---------- PROXY (same pattern as Federal/Bonus) ----------
const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

// ✅ YEAR CONFIG (same pattern as BonusTaxCalculator)
const YEAR_CONFIG = {
  2025: {
    disclaimer:
      "This calculator provides estimates only. Consult a tax professional for personalized advice.",
  },
  2026: {
    disclaimer:
      "This calculator provides estimates only. Consult a tax professional for personalized advice.",
  },
};

function UncleKamBusinessTaxCalculator({
  year = 2025,
  calculatorName = "Small Business Tax Calculator",
  // keep prop for compatibility, but we’ll send exactly like Federal (via proxy)
  endpoint = PROXY_URL,
}) {
  const config = YEAR_CONFIG[year] || YEAR_CONFIG[2025];

  const [formData, setFormData] = useState({
    entityType: "",
    revenue: "",
    totalExpenses: "",
    mealsExpenses: "",
    estimatedTaxPayments: "",
    otherIncome: "",
    filingStatus: "",
    deductionType: "Standard Deduction",
    itemizedDeductions: "",
  });

  const [result, setResult] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const entityTypes = [
    "C-Corp",
    "S-Corp",
    "LLC - S-Corp Election",
    "LLC - Single Member",
    "LLC - Partnership",
  ];

  const filingStatuses = [
    "Single",
    "Married Filing Jointly",
    "Married Filing Separately",
    "Head of Household",
  ];

  const deductionTypes = ["Standard Deduction", "Itemized Deductions"];

  // ---- STANDARD DEDUCTION BY YEAR ----
  const STANDARD_DEDUCTION_BY_YEAR = {
    2025: {
      single: 15000,
      married: 30000,
      head: 22500,
      separate: 15000,
    },
    2026: {
      single: 16100,
      married: 32200,
      head: 24150,
      separate: 16100,
    },
  };

  // ---- ORDINARY INCOME BRACKETS BY YEAR ----
  const TAX_BRACKETS_BY_YEAR = {
    2025: {
      single: [
        { rate: 0.1, upTo: 11925 },
        { rate: 0.12, upTo: 48475 },
        { rate: 0.22, upTo: 103350 },
        { rate: 0.24, upTo: 197300 },
        { rate: 0.32, upTo: 250525 },
        { rate: 0.35, upTo: 626350 },
        { rate: 0.37, upTo: Infinity },
      ],
      married: [
        { rate: 0.1, upTo: 23850 },
        { rate: 0.12, upTo: 96950 },
        { rate: 0.22, upTo: 206700 },
        { rate: 0.24, upTo: 394600 },
        { rate: 0.32, upTo: 501050 },
        { rate: 0.35, upTo: 751600 },
        { rate: 0.37, upTo: Infinity },
      ],
      head: [
        { rate: 0.1, upTo: 17000 },
        { rate: 0.12, upTo: 64850 },
        { rate: 0.22, upTo: 103350 },
        { rate: 0.24, upTo: 197300 },
        { rate: 0.32, upTo: 250525 },
        { rate: 0.35, upTo: 626350 },
        { rate: 0.37, upTo: Infinity },
      ],
      separate: [
        { rate: 0.1, upTo: 11925 },
        { rate: 0.12, upTo: 48475 },
        { rate: 0.22, upTo: 103350 },
        { rate: 0.24, upTo: 197300 },
        { rate: 0.32, upTo: 250525 },
        { rate: 0.35, upTo: 375800 },
        { rate: 0.37, upTo: Infinity },
      ],
    },
    2026: {
      single: [
        { rate: 0.1, upTo: 12400 },
        { rate: 0.12, upTo: 50400 },
        { rate: 0.22, upTo: 105700 },
        { rate: 0.24, upTo: 201775 },
        { rate: 0.32, upTo: 256225 },
        { rate: 0.35, upTo: 640600 },
        { rate: 0.37, upTo: Infinity },
      ],
      married: [
        { rate: 0.1, upTo: 24800 },
        { rate: 0.12, upTo: 100800 },
        { rate: 0.22, upTo: 211400 },
        { rate: 0.24, upTo: 403550 },
        { rate: 0.32, upTo: 512450 },
        { rate: 0.35, upTo: 768700 },
        { rate: 0.37, upTo: Infinity },
      ],
      separate: [
        { rate: 0.1, upTo: 12400 },
        { rate: 0.12, upTo: 50400 },
        { rate: 0.22, upTo: 105700 },
        { rate: 0.24, upTo: 201775 },
        { rate: 0.32, upTo: 256225 },
        { rate: 0.35, upTo: 384350 },
        { rate: 0.37, upTo: Infinity },
      ],
      head: [
        { rate: 0.1, upTo: 17700 },
        { rate: 0.12, upTo: 67450 },
        { rate: 0.22, upTo: 105700 },
        { rate: 0.24, upTo: 201750 },
        { rate: 0.32, upTo: 256200 },
        { rate: 0.35, upTo: 640600 },
        { rate: 0.37, upTo: Infinity },
      ],
    },
  };

  // ---- Self-employment parameters BY YEAR ----
  const SS_WAGE_BASE_BY_YEAR = {
    2025: 176_100,
    2026: 176_100,
  };

  const ADDL_MEDICARE_THRESHOLDS = {
    single: 200_000,
    head: 200_000,
    married: 250_000,
    separate: 125_000,
  };

  const STANDARD_DEDUCTION =
    STANDARD_DEDUCTION_BY_YEAR[year] || STANDARD_DEDUCTION_BY_YEAR[2025];
  const TAX_BRACKETS = TAX_BRACKETS_BY_YEAR[year] || TAX_BRACKETS_BY_YEAR[2025];
  const SS_WAGE_BASE = SS_WAGE_BASE_BY_YEAR[year] || SS_WAGE_BASE_BY_YEAR[2025];

  // ✅ keep latest payload for emailing (Bonus style)
  const latestPayloadRef = useRef({
    inputs_json: null,
    results_json: null,
    pdf_notes: null,
  });

  const resultsRef = useRef(null);
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  // ---------- Gating storage helpers (24h, shared across calculators) ----------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  // gate state
  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  // modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // optional: if they change inputs after calculating, hide results until next click
  useEffect(() => {
    setHasCalculated(false);
    setResult(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
  }, [
    formData.entityType,
    formData.revenue,
    formData.totalExpenses,
    formData.mealsExpenses,
    formData.estimatedTaxPayments,
    formData.otherIncome,
    formData.filingStatus,
    formData.deductionType,
    formData.itemizedDeductions,
    year,
  ]);

  const parseNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const clamp0 = (n) => Math.max(0, n || 0);

  const calculateIncomeTax = (taxable, brackets) => {
    let tax = 0;
    let marginalRate = 0;
    let lower = 0;

    for (const { rate, upTo } of brackets) {
      const upper = Math.min(taxable, upTo);
      if (upper > lower) {
        tax += (upper - lower) * rate;
        marginalRate = rate;
        lower = upper;
      }
      if (taxable <= upTo) break;
    }
    return { tax, marginalRate };
  };

  const computeSelfEmploymentTax = (businessNet, filingKey) => {
    if (businessNet <= 0) return { seTax: 0, halfSE: 0 };

    const seNet = 0.9235 * businessNet;
    const socialSecurity = Math.min(seNet, SS_WAGE_BASE) * 0.124;
    const medicare = seNet * 0.029;
    const addlThreshold = ADDL_MEDICARE_THRESHOLDS[filingKey] ?? 200000;
    const additionalMedicare = Math.max(0, seNet - addlThreshold) * 0.009;

    const seTax = socialSecurity + medicare + additionalMedicare;
    return { seTax, halfSE: seTax * 0.5 };
  };

  // ✅ Only include inputs that are displayed
  const buildInputsJsonDisplayed = () => {
    const inputs = {
      entityType: formData.entityType,
      revenue: parseNum(formData.revenue),
      totalExpenses: parseNum(formData.totalExpenses),
      mealsExpenses: parseNum(formData.mealsExpenses),
      estimatedTaxPayments: parseNum(formData.estimatedTaxPayments),
      filingStatus: formData.filingStatus,
      otherIncome: parseNum(formData.otherIncome),
      deductionType: formData.deductionType,
      year: Number(year),
      calculatorName,
    };

    if (formData.deductionType === "Itemized Deductions") {
      inputs.itemizedDeductions = parseNum(formData.itemizedDeductions);
    }

    return inputs;
  };

  // ✅ Only include result fields that are displayed (+ note)
  const buildResultsJsonDisplayed = (computed) => {
    if (!computed) return null;

    const results = {
      businessNetIncome: computed.businessNetIncome,
      totalTaxableIncome: computed.totalTaxableIncome,
      deductionUsed: computed.deductionUsed,
      incomeTax: computed.incomeTax,
      selfEmploymentTax: computed.selfEmploymentTax,
      marginalRate: computed.marginalRate,
      effectiveRate: computed.effectiveRate,
      totalTaxLiability: computed.totalTaxLiability,
      estimatedPayments: computed.estimatedPayments,
      estimatedTaxDue: computed.estimatedTaxDue,
    };

    if (computed.warning) results.warning = computed.warning;
    if (computed.note) results.note = computed.note;

    return results;
  };

  // ✅ Bonus-style computeResults: returns {inputs_json, results_json, pdf_notes}
  const computeResults = () => {
    const revenue = parseNum(formData.revenue);
    const expenses = parseNum(formData.totalExpenses);
    const meals = parseNum(formData.mealsExpenses);
    const estimated = parseNum(formData.estimatedTaxPayments);
    const otherIncome = parseNum(formData.otherIncome);
    const itemized = parseNum(formData.itemizedDeductions);

    const filingKey = (() => {
      if (formData.filingStatus === "Married Filing Jointly") return "married";
      if (formData.filingStatus === "Married Filing Separately") return "separate";
      if (formData.filingStatus === "Head of Household") return "head";
      return "single";
    })();

    const mealsDeductible = 0.5 * meals;
    const businessNetIncome = clamp0(revenue - (expenses + mealsDeductible));

    const deduction =
      formData.deductionType === "Standard Deduction"
        ? STANDARD_DEDUCTION[filingKey] || 0
        : clamp0(itemized);

    const isSOrElect =
      formData.entityType === "S-Corp" || formData.entityType === "LLC - S-Corp Election";
    const isSoleProp = formData.entityType === "LLC - Single Member";
    const isPartnership = formData.entityType === "LLC - Partnership";
    const isPassThrough = isSOrElect || isSoleProp || isPartnership;

    let computed;

    if (formData.entityType === "C-Corp") {
      const corpTax = clamp0(businessNetIncome * 0.21);
      const totalTaxLiability = corpTax;
      const estimatedTaxDue = totalTaxLiability - estimated;
      const effectiveRate =
        businessNetIncome > 0 ? +(totalTaxLiability / businessNetIncome).toFixed(4) : 0;

      computed = {
        businessNetIncome,
        totalTaxableIncome: businessNetIncome,
        deductionUsed: 0,
        incomeTax: corpTax,
        selfEmploymentTax: 0,
        totalTaxLiability,
        estimatedPayments: estimated,
        estimatedTaxDue,
        effectiveRate,
        marginalRate: 21,
        isRefund: estimatedTaxDue < 0,
        note:
          "C-Corp taxed at a flat 21% at the entity level; personal-level items (SD/QBI) not applied here.",
      };
    } else {
      const { seTax, halfSE } =
        isPassThrough && !isSOrElect
          ? computeSelfEmploymentTax(businessNetIncome, filingKey)
          : { seTax: 0, halfSE: 0 };

      const agi = clamp0(businessNetIncome + otherIncome - halfSE);
      const taxablePreQBI = clamp0(agi - deduction);

      let qbiDeduction = 0;
      if (isPassThrough) {
        const qbiBase = clamp0(isSOrElect ? businessNetIncome : businessNetIncome - halfSE);
        if (qbiBase > 0 && taxablePreQBI > 0) {
          const tentative = 0.2 * qbiBase;
          const cap = 0.2 * taxablePreQBI;
          qbiDeduction = Math.min(tentative, cap);
        }
      }

      const taxableAfterQBI = clamp0(taxablePreQBI - qbiDeduction);

      const { tax: incomeTax, marginalRate } = calculateIncomeTax(
        taxableAfterQBI,
        TAX_BRACKETS[filingKey] || TAX_BRACKETS.single
      );

      const totalTaxLiability = incomeTax + seTax;
      const estimatedTaxDue = totalTaxLiability - estimated;
      const effectiveRate = agi > 0 ? +(totalTaxLiability / agi).toFixed(4) : 0;

      computed = {
        businessNetIncome,
        totalTaxableIncome: agi,
        deductionUsed: deduction,
        incomeTax,
        selfEmploymentTax: seTax,
        totalTaxLiability,
        estimatedPayments: estimated,
        estimatedTaxDue,
        effectiveRate,
        marginalRate: +(marginalRate * 100).toFixed(1),
        isRefund: estimatedTaxDue < 0,
        note:
          isPassThrough && qbiDeduction > 0
            ? "Includes a Section 199A (QBI) deduction subject to the 20% of taxable income cap."
            : undefined,
      };
    }

    const inputs_json = buildInputsJsonDisplayed();
    const results_json = buildResultsJsonDisplayed(computed);

    // ✅ pdf_notes (Bonus style)
    const pdf_notes = {
      disclaimer: config.disclaimer,
      // you said include note because backend updated
      note: computed?.note,
      footer: {
        cta_title: "Want a personalized tax strategy? Speak with an Uncle Kam expert.",
        cta_subtitle: "Visit us at unclekam.com",
      },
    };

    return { inputs_json, results_json, pdf_notes, computedRaw: computed };
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Math.abs(Number(num) || 0));
  };

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ UPDATED: SEND REQUEST EXACTLY LIKE BONUS (includes pdf_notes)
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);

    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: payload.pdf_notes,
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();

      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    const newE = String(newEmail || "").trim().toLowerCase();
    writeUnlock({ email: newE });
    setStoredEmail(newE);
    await sendResultsToEmail(newE);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ like Bonus: compute, set state, store payload, then open gate
  const handleViewResultsClick = () => {
    const computed = computeResults();
    if (!computed?.results_json) {
      setResult(null);
      setHasCalculated(false);
      latestPayloadRef.current = { inputs_json: null, results_json: null, pdf_notes: null };
      return;
    }

    setResult(computed.computedRaw); // for UI (needs raw fields like effectiveRate etc)
    setHasCalculated(true);

    latestPayloadRef.current = {
      inputs_json: computed.inputs_json,
      results_json: computed.results_json,
      pdf_notes: computed.pdf_notes,
    };

    openGateFlow();
  };

  return (
    <>
      <div className="container py-5">
        <div className="row g-4 d-flex justify-content-center">
          <div className="col-md-6">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary text-white">
                <div className="bg-primary text-white p-2 rounded">
                  Professional Business Tax Calculator ({year})
                </div>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Entity Type</label>
                  <select
                    className="form-select"
                    value={formData.entityType}
                    onChange={(e) => handleInputChange("entityType", e.target.value)}
                  >
                    <option value="">Select</option>
                    {entityTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Annual Revenue"
                  value={formData.revenue}
                  onChange={(e) => handleInputChange("revenue", e.target.value)}
                />
                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Total Business Expenses"
                  value={formData.totalExpenses}
                  onChange={(e) => handleInputChange("totalExpenses", e.target.value)}
                />
                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Meals & Entertainment Expenses"
                  value={formData.mealsExpenses}
                  onChange={(e) => handleInputChange("mealsExpenses", e.target.value)}
                />
                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Estimated Tax Payments Made"
                  value={formData.estimatedTaxPayments}
                  onChange={(e) => handleInputChange("estimatedTaxPayments", e.target.value)}
                />

                <div className="mb-3">
                  <label className="form-label">Filing Status</label>
                  <select
                    className="form-select"
                    value={formData.filingStatus}
                    onChange={(e) => handleInputChange("filingStatus", e.target.value)}
                  >
                    <option value="">Select</option>
                    {filingStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="number"
                  className="form-control mb-3"
                  placeholder="Other Income"
                  value={formData.otherIncome}
                  onChange={(e) => handleInputChange("otherIncome", e.target.value)}
                />

                <div className="mb-3">
                  <label className="form-label">Deduction Type</label>
                  <select
                    className="form-select"
                    value={formData.deductionType}
                    onChange={(e) => handleInputChange("deductionType", e.target.value)}
                  >
                    {deductionTypes.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.deductionType === "Itemized Deductions" && (
                  <input
                    type="number"
                    className="form-control mb-3"
                    placeholder="Total Itemized Deductions"
                    value={formData.itemizedDeductions}
                    onChange={(e) => handleInputChange("itemizedDeductions", e.target.value)}
                  />
                )}

                <button
                  className="btn w-100 mt-2"
                  onClick={handleViewResultsClick}
                  style={{ background: "#0f8a4c", borderColor: "#0f8a4c", color: "white" }}
                >
                  View Results
                </button>

                <div className="mt-3">
                  <small className="text-muted">
                    Click <strong>View Results</strong> to unlock your calculation.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3" ref={resultsRef}>
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary-subtle text-dark p-0">
                <div className="bg-success text-white p-2 rounded m-0" style={{ background: "#0f8a4c" }}>
                  Calculation Preview
                </div>
              </div>

              <div className="card-body bg-white p-4">
                <div
                  className="unk-results-wrap"
                  style={{
                    background: !unlocked
                      ? "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.92))"
                      : "transparent",
                  }}
                >
                  {!unlocked && <div className="unk-bg-blur" />}

                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div className={!unlocked ? "unk-results-blur" : ""}>
                      {hasCalculated && result ? (
                        <div className="small">
                          {result.warning && (
                            <div className="alert alert-danger d-flex align-items-center small" role="alert">
                              <i className="bi bi-exclamation-triangle-fill me-2"></i>
                              {result.warning}
                            </div>
                          )}

                          <div className="row mb-2">
                            <div className="col-auto fw-semibold">Business Net Income:</div>
                            <div className="col text-end">{formatCurrency(result.businessNetIncome)}</div>
                          </div>

                          <div className="row mb-2">
                            <div className="col-auto fw-semibold">Total Taxable Income:</div>
                            <div className="col text-end">{formatCurrency(result.totalTaxableIncome)}</div>
                          </div>

                          <div className="row mb-2">
                            <div className="col-auto fw-semibold">Deduction Used:</div>
                            <div className="col text-end">{formatCurrency(result.deductionUsed)}</div>
                          </div>

                          <div className="row mb-2">
                            <div className="col-auto fw-semibold">Income Tax:</div>
                            <div className="col text-end">{formatCurrency(result.incomeTax)}</div>
                          </div>

                          {result.selfEmploymentTax !== undefined && (
                            <div className="row mb-2">
                              <div className="col-auto fw-semibold">Self-Employment Tax:</div>
                              <div className="col text-end">{formatCurrency(result.selfEmploymentTax)}</div>
                            </div>
                          )}

                          <div className="row mb-2">
                            <div className="col-auto fw-semibold">Marginal Tax Rate:</div>
                            <div className="col text-end">{result.marginalRate}%</div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-auto fw-semibold">Effective Tax Rate:</div>
                            <div className="col text-end">{(result.effectiveRate * 100).toFixed(1)}%</div>
                          </div>

                          <hr />

                          <div className="row mb-2">
                            <div className="col-auto fw-bold text-success">Total Tax Liability:</div>
                            <div className="col text-end fw-bold text-success">
                              {formatCurrency(result.totalTaxLiability)}
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-auto fw-semibold">Estimated Payments Made:</div>
                            <div className="col text-end text-muted">-{formatCurrency(result.estimatedPayments)}</div>
                          </div>

                          <div className="row">
                            <div
                              className="col-auto fw-bold"
                              style={{
                                color: result.isRefund ? "#198754" : "#dc3545",
                                fontSize: "1.25rem",
                              }}
                            >
                              {result.isRefund ? "Estimated Refund:" : "Estimated Tax Due:"}
                            </div>
                            <div
                              className="col text-end fw-bold"
                              style={{
                                color: result.isRefund ? "#198754" : "#dc3545",
                                fontSize: "1.25rem",
                              }}
                            >
                              {formatCurrency(result.estimatedTaxDue)}
                            </div>
                          </div>

                          {result.note && (
                            <div className="alert alert-info small mt-4">
                              <i className="bi bi-info-circle-fill me-2"></i>
                              {result.note}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-muted">
                          Enter information and click <strong>View Results</strong>.
                        </p>
                      )}
                    </div>

                    {!unlocked && (
                      <div
                        className="unk-results-overlay"
                        onClick={() => {
                          if (!hasCalculated || !result) handleViewResultsClick();
                          else openGateFlow();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            if (!hasCalculated || !result) handleViewResultsClick();
                            else openGateFlow();
                          }
                        }}
                        aria-label="Results locked. Open unlock modal."
                      >
                        <div className="unk-overlay-card">
                          <div className="unk-overlay-title">Results Locked</div>
                          <div className="unk-overlay-body">
                            Enter your name and email to view your results instantly.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ disclaimer displayed like Bonus */}
        <div className="text-center text-muted small mt-4 w-50 mx-auto">
          <p>{config.disclaimer}</p>
        </div>
      </div>

      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json}
        inputsJson={latestPayloadRef.current?.inputs_json}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={() => {
          setShowConfirmModal(false);
          setShowUpdateEmailModal(true);
        }}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </>
  );
}

export default UncleKamBusinessTaxCalculator;
